/**
 * VerifyModal - Popup modal wrapper for gesture verification
 * Maintains exact styling from verify-test.html but in modal format
 */

import { VerifyScreen } from '../screens/VerifyScreen.js';

export class VerifyModal {
    constructor(options = {}) {
        this.siteKey = options.siteKey;
        this.applicationId = options.applicationId;
        this.apiUrl = options.apiUrl || (window.location.hostname === 'localhost' 
            ? 'ws://localhost:8001' 
            : 'wss://verify.dexasign.com');
        this.onComplete = options.onComplete;
        this.onCancel = options.onCancel;
        this.onError = options.onError;
        
        this.screen = null;
        this.overlay = null;
        this.modal = null;
        this.isOpen = false;
    }

    /**
     * Create modal DOM structure
     */
    createModal() {
        // Create overlay — flex-centering is far more reliable on iOS Safari than
        // the absolute+transform trick, which can drift when the page has scrolled.
        this.overlay = document.createElement('div');
        this.overlay.className = 'dexasign-overlay';
        this.overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.75);
            z-index: 999999;
            display: none;
            align-items: center;
            justify-content: center;
            overflow-y: auto;
            animation: fadeIn 0.2s ease;
        `;

        // Create modal container
        this.modal = document.createElement('div');
        this.modal.className = 'dexasign-modal';
        this.modal.style.cssText = `
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            max-width: 700px;
            width: 90%;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            animation: slideIn 0.3s ease;
            flex-shrink: 0;
        `;

        // Modal header — flex-shrink:0 keeps it pinned; only the content scrolls
        const header = document.createElement('div');
        header.style.cssText = `
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 24px;
            border-bottom: 1px solid #E5E7EB;
            background: white;
            border-radius: 16px 16px 0 0;
            flex-shrink: 0;
        `;

        const logo = document.createElement('div');
        logo.style.cssText = `
            display: flex;
            align-items: center;
            gap: 12px;
            font-weight: 700;
            font-size: 24px;
            color: #0A2540;
            letter-spacing: -0.02em;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        `;
        logo.innerHTML = `
            <img src="/static/Dexasign-Logo.svg" alt="Dexasign" width="32" height="32" style="flex-shrink: 0;">
            <span style="white-space: nowrap; line-height: 32px;">Dexasign Verify</span>
        `;

        const closeBtn = document.createElement('button');
        closeBtn.innerHTML = '&times;';
        closeBtn.style.cssText = `
            background: none;
            border: none;
            font-size: 32px;
            color: #6B7280;
            cursor: pointer;
            padding: 0;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            line-height: 1;
            transition: color 0.2s;
        `;
        closeBtn.addEventListener('mouseenter', () => closeBtn.style.color = '#0A2540');
        closeBtn.addEventListener('mouseleave', () => closeBtn.style.color = '#6B7280');
        closeBtn.addEventListener('click', () => this.hide());

        header.appendChild(logo);
        header.appendChild(closeBtn);

        // Content container — flex:1 fills remaining space, overflow-y:auto scrolls
        const content = document.createElement('div');
        content.id = 'verify-modal-content';
        content.style.cssText = `
            padding: 24px;
            background: #F9FAFB;
            flex: 1;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
        `;

        this.modal.appendChild(header);
        this.modal.appendChild(content);
        this.overlay.appendChild(this.modal);

        // Add animations and modal-specific styles
        const style = document.createElement('style');
        style.textContent = `
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            @keyframes slideIn {
                from { 
                    opacity: 0;
                    transform: scale(0.96);
                }
                to { 
                    opacity: 1;
                    transform: scale(1);
                }
            }
            
            /* Modal-specific overrides for VerifyScreen */
            .dexasign-modal .auth-content {
                padding: 0 !important;
                margin: 0 !important;
            }
            
            .dexasign-modal .auth-prompt h1 {
                font-size: 24px !important;
                margin-bottom: 16px !important;
            }
            
            .dexasign-modal .auth-footer {
                padding: 12px 0 0 0 !important;
                font-size: 12px !important;
            }
            
            .dexasign-modal .video-wrapper {
                margin: 20px 0 !important;
            }

            /* On mobile, recover vertical space by trimming the large margins
               around the viewfinder and the gap between flex items — without
               touching font sizes, element sizes, or spacing within any element.
               The canvas keeps its natural aspect-ratio height so all planets
               remain fully visible. */
            @media (max-width: 768px) {
                .dexasign-modal {
                    max-height: 80vh !important;
                    width: 94% !important;
                    border-radius: 12px !important;
                }
                /* Force landscape 4:3 aspect ratio on the viewfinder so it
                   always matches Chrome DevTools preview regardless of whether
                   the iOS camera delivers portrait video dimensions. */
                .dexasign-modal .video-wrapper {
                    aspect-ratio: 4 / 3 !important;
                    height: auto !important;
                    margin: 6px 0 !important;
                    overflow: hidden !important;
                }
                .dexasign-modal .video-wrapper video {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: cover !important;
                }
                .dexasign-modal .video-wrapper canvas {
                    position: absolute !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100% !important;
                    height: 100% !important;
                }
                .dexasign-modal .auth-content {
                    gap: 10px !important;
                }
            }
        `;
        document.head.appendChild(style);

        document.body.appendChild(this.overlay);
    }

    /**
     * Show the modal
     */
    show() {
        if (this.isOpen) return;

        if (!this.overlay) {
            this.createModal();
        }

        this.overlay.style.display = 'flex';
        document.body.style.overflow = 'hidden';
        this.isOpen = true;

        // Initialize VerifyScreen inside modal
        this.initVerifyScreen();
    }

    /**
     * Hide the modal
     */
    hide() {
        if (!this.isOpen) return;

        this.overlay.style.display = 'none';
        document.body.style.overflow = 'auto';
        this.isOpen = false;

        // Cleanup screen
        if (this.screen) {
            this.screen.destroy();
            this.screen = null;
        }

        // Clear content
        const content = this.modal.querySelector('#verify-modal-content');
        if (content) {
            content.innerHTML = '';
        }

        if (this.onCancel) {
            this.onCancel();
        }
    }

    /**
     * Initialize VerifyScreen component
     */
    initVerifyScreen() {
        const container = this.modal.querySelector('#verify-modal-content');

        this.screen = new VerifyScreen({
            container: container,
            apiUrl: this.apiUrl,
            applicationId: this.applicationId,
            onSuccess: (captures) => {

                
                // Generate token (temporary - will be server-side later)
                const token = this.generateToken(captures);

                if (this.onComplete) {
                    this.onComplete({
                        token: token,
                        gestures: captures,
                        timestamp: Date.now()
                    });
                }

                // Auto-close modal after success
                setTimeout(() => this.hide(), 1000);
            },
            onError: (error) => {
                console.error('❌ Verification error:', error);
                if (this.onError) {
                    this.onError(error);
                }
            }
        });

        this.screen.start();
    }

    /**
     * Generate temporary verification token
     * TODO: This should be server-side with HMAC signing
     */
    generateToken(captures) {
        const data = {
            siteKey: this.siteKey,
            timestamp: Date.now(),
            gestureCount: captures.length
        };
        
        // Simple base64 encoding for now (NOT secure - placeholder)
        const tokenData = btoa(JSON.stringify(data));
        return `vfy_${tokenData.substring(0, 32)}`;
    }

    /**
     * Destroy modal and cleanup
     */
    destroy() {
        this.hide();
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        this.overlay = null;
        this.modal = null;
    }
}
